// Asset Hitch Log exports (CSV + branded landscape PDF) — vehicle-first mirror of
// assetLogExport.js so the Trailer Hitch Log / Equipment Attachment Log stay visually
// consistent with the Vehicle Asset Log Book exports.
import { formatDuration, getFileTimestamp } from './timezone';
import {
  newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, downloadBlob, rowsToCsv,
  safeName, fmtCoords, tripLocMapsUrl, csvMapsLink,
  SLATE_900, SLATE_600, SLATE_500, GREEN, formatExportDateTime,
} from './tripExport';

const stamp = () => getFileTimestamp();
const HEAD_BG = [241, 245, 249];
const ROW_BG = [248, 250, 252];

const entryCsvRows = (e, idx) => ([
  [`Session #${idx + 1}`, e.is_active ? 'Attached now' : 'Detached'],
  ['Vehicle', e.vehicle_name || '-'],
  ['Source', e.source_label || '-'],
  ['Beacon / Credential', e.credential_identifier || '-'],
  ['Attached', formatExportDateTime(e.started_at)],
  ['Detached', e.is_active ? 'Still attached' : formatExportDateTime(e.ended_at)],
  ['Duration', e.duration_seconds != null ? formatDuration(e.duration_seconds) : '-'],
  ['How it ended', e.is_active ? '-' : (e.ended_reason || '-')],
  ['Attach location', csvMapsLink(e.location)],
  [e.is_active ? 'Last seen location' : 'Detach location', csvMapsLink(e.end_location)],
]);

export const exportHitchEntryCsv = (entry, assetName, idx = 0) => {
  const rows = [['Asset', assetName || ''], []].concat(entryCsvRows(entry, idx));
  downloadBlob(rowsToCsv(rows), `${safeName(assetName)}-hitch-log-entry-${idx + 1}-${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

export const exportAllHitchCsv = (entries, assetName, utilisation = []) => {
  let rows = [['Asset', assetName || ''], ['Sessions', String((entries || []).length)], ['Exported', formatExportDateTime(new Date().toISOString())], []];
  if ((utilisation || []).length) {
    rows.push(['Utilisation — attached time & sessions per vehicle']);
    rows.push(['Vehicle', 'Sessions', 'Attached time', 'Attached now']);
    (utilisation || []).forEach((u) => rows.push([
      u.vehicle_name || 'Vehicle', String(u.sessions ?? '-'),
      u.total_attached_seconds != null ? formatDuration(u.total_attached_seconds) : '-',
      u.active ? 'Yes' : 'No',
    ]));
    rows.push([]);
  }
  (entries || []).forEach((e, idx) => { rows = rows.concat(entryCsvRows(e, idx), [[], []]); });
  downloadBlob(rowsToCsv(rows), `${safeName(assetName)}-hitch-log_${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

const COLS = [
  { label: 'Vehicle', w: 140 },
  { label: 'Source', w: 85 },
  { label: 'Beacon / Credential', w: 140 },
  { label: 'Attached -> Detached', w: null },
  { label: 'How it ended', w: 120 },
  { label: 'Duration', w: 65 },
];

const drawHitchTable = (doc, layout, entries, startY) => {
  const { M, CONTENT_W, PAGE_H } = layout;
  const fixed = COLS.reduce((acc, c) => acc + (c.w || 0), 0);
  const cols = COLS.map((c) => ({ ...c, w: c.w == null ? Math.max(120, CONTENT_W - fixed) : c.w }));
  const xs = [];
  let cx = M;
  cols.forEach((c) => { xs.push(cx); cx += c.w; });
  const ROW_H = 16;
  let y = startY;
  const drawHeader = () => {
    doc.setFillColor(...HEAD_BG);
    doc.rect(M, y, CONTENT_W, ROW_H, 'F');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600);
    cols.forEach((c, i) => doc.text(c.label, xs[i] + 4, y + 11));
    y += ROW_H;
  };
  drawHeader();
  doc.setFont(undefined, 'normal'); doc.setFontSize(8);
  if ((entries || []).length === 0) {
    doc.setTextColor(...SLATE_500);
    doc.text('No vehicle attachments found for the selected filters.', M + 4, y + 11);
    return y + ROW_H;
  }
  (entries || []).forEach((e, i) => {
    const rangeText = `${formatExportDateTime(e.started_at)} -> ${e.is_active ? 'attached' : formatExportDateTime(e.ended_at)}`;
    // Never truncate the detach time: wrap the range onto a second line when needed.
    const rangeLines = doc.splitTextToSize(rangeText, cols[3].w - 8).slice(0, 2);
    const rowH = rangeLines.length > 1 ? 26 : ROW_H;
    if (y + rowH > PAGE_H - 44) { doc.addPage(); y = 44; drawHeader(); doc.setFont(undefined, 'normal'); doc.setFontSize(8); }
    if (i % 2 === 1) { doc.setFillColor(...ROW_BG); doc.rect(M, y, CONTENT_W, rowH, 'F'); }
    const cells = [
      String(e.vehicle_name || '-'),
      String(e.source_label || '-'),
      String(e.credential_identifier || '-'),
      null, // range drawn separately below
      e.is_active ? '-' : String(e.ended_reason || '-'),
      e.duration_seconds != null ? formatDuration(e.duration_seconds) : '-',
    ];
    cells.forEach((val, ci) => {
      if (val == null) return;
      doc.setTextColor(...SLATE_900);
      const fit = doc.splitTextToSize(val, cols[ci].w - 8)[0] || '';
      doc.text(fit, xs[ci] + 4, y + 11);
    });
    doc.setTextColor(...(e.is_active ? GREEN : SLATE_900));
    rangeLines.forEach((line, li) => doc.text(line, xs[3] + 4, y + 11 + li * 10));
    y += rowH;
  });
  return y;
};

const drawVehicleUtilisation = (doc, layout, utilisation, startY) => {
  const { M, PAGE_H, RIGHT } = layout;
  if (!(utilisation || []).length) return startY;
  let y = startY + 6;
  if (y + 24 > PAGE_H - 44) { doc.addPage(); y = 44; }
  doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
  doc.text('Utilisation — attached time & sessions per vehicle', M, y); y += 14;
  doc.setFontSize(8); doc.setFont(undefined, 'normal');
  (utilisation || []).forEach((u) => {
    if (y + 13 > PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setTextColor(...SLATE_900);
    doc.text(`${u.vehicle_name || 'Vehicle'}${u.active ? ' — attached now' : ''}`, M, y);
    doc.setTextColor(...SLATE_600);
    doc.text(`${u.sessions ?? 0} ${u.sessions === 1 ? 'session' : 'sessions'}  |  ${u.total_attached_seconds != null ? formatDuration(u.total_attached_seconds) : '-'} attached`, RIGHT, y, { align: 'right' });
    y += 13;
  });
  return y;
};

export const exportHitchEntryPdf = async (entry, assetName, idx = 0) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Asset Hitch Session', assetName);
  y = drawHitchTable(doc, layout, [entry], y);
  const startUrl = tripLocMapsUrl(entry.location);
  const endUrl = tripLocMapsUrl(entry.end_location);
  if (startUrl || endUrl) {
    y += 14;
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(37, 99, 235);
    let lx = layout.M;
    if (startUrl) { const t = `Attach: ${fmtCoords(entry.location)}`; doc.textWithLink(t, lx, y, { url: startUrl }); lx += doc.getTextWidth(t) + 24; }
    if (endUrl) { const t = `${entry.is_active ? 'Last seen' : 'Detach'}: ${fmtCoords(entry.end_location)}`; doc.textWithLink(t, lx, y, { url: endUrl }); }
  }
  addFooters(doc, layout);
  doc.save(`${safeName(assetName)}-hitch-log-entry-${idx + 1}-${stamp()}.pdf`);
};

export const buildAllHitchPdf = async (entries, assetName, utilisation = [], title = 'Asset Hitch Log') => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, title, `${assetName || '-'} \u00b7 ${(entries || []).length} sessions`);
  y = drawHitchTable(doc, layout, entries, y);
  y = drawVehicleUtilisation(doc, layout, utilisation, y);
  addFooters(doc, layout);
  return { doc, filename: `${safeName(assetName)}-hitch-log_${stamp()}.pdf` };
};

export const exportAllHitchPdf = async (entries, assetName, utilisation = [], title) => {
  const { doc, filename } = await buildAllHitchPdf(entries, assetName, utilisation, title);
  doc.save(filename);
};
